import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import CTextField from "../../components/CTextField";
import { roleType } from "../../constant/constants";
import { fetchCriticalSkills } from "../../slices/trainingStrategyInputSlice";
import { isCompanyPrivileged } from "../../utils/roleUtils";

const CriticalSkills = () => {
  const localUser = JSON.parse(localStorage.getItem("sdfUser"));
  const userRole = localUser?.role;
  const companyId = localUser?.company_id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [criticalSkills, setCriticalSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isCompanyPrivileged(userRole) && companyId) {
      const loadCriticalSkills = async () => {
        setLoading(true);
        try {
          const result = await dispatch(fetchCriticalSkills());
          if (result.payload) {
            setCriticalSkills(result.payload);
          } else if (result.error) {
            console.error("Error fetching critical skills:", result.error);
          }
        } catch (error) {
          console.error("Error loading critical skills:", error);
        } finally {
          setLoading(false);
        }
      };
      loadCriticalSkills();
    }
  }, [userRole, companyId, dispatch]);

  // Define columns for the DataGrid
  const criticalSkillsColumns = useMemo(() => [
     {
      field: "skill_name",
      headerName: "Skill Name",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 3,
    },
    {
      field: "level",
      headerName: "Rank",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const levelLabels = {
          essential: "Essential",
          intermediate: "Intermediate",
          expert: "Expert",
        };
        return levelLabels[params.row.level] || 
          (params.row.level ? params.row.level.charAt(0).toUpperCase() + params.row.level.slice(1) : "-");
      },
    },
    {
      field: "importance",
      headerName: "Importance",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => params.row.importance || "-",
    },
  ], [criticalSkills]);

  // Filter critical skills based on search term
  const filteredCriticalSkills = useMemo(() => {
    if (!searchTerm.trim()) {
      return criticalSkills;
    }
    const searchLower = searchTerm.toLowerCase();
    return criticalSkills.filter((skill) => {
      return (
        skill.skill_name?.toLowerCase().includes(searchLower) ||
        skill.description?.toLowerCase().includes(searchLower) ||
        skill.level?.toLowerCase().includes(searchLower) ||
        skill.importance?.toString().includes(searchLower)
      );
    });
  }, [criticalSkills, searchTerm]);

  return (
    <div className="h-[calc(100vh-116px)] relative flex flex-col">
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
        Critical Skills
      </h2>
      
      {/* Instructions Section */}
      <div className="border-2 border-blue-500 bg-white rounded-lg p-4 md:mb-6 mb-4 relative">
        <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
          Instructions
        </p>
        <div>
          <p className="font-medium mb-0 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            View all critical skills that have been identified for your organization. 
            Use the search bar to filter skills by name, description, level, or importance. 
            Click "View Training" to navigate to a screen where you can add new trainings or find all existing trainings related to these critical skills.
          </p>
        </div>
      </div>

      <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden flex flex-col flex-1">
        <div className="flex items-center justify-between p-4 border-b border-blue-200 gap-4">
          {/* <h3 className="text-lg font-semibold text-blue-500">
            Critical Skills
          </h3> */}
          <div className="flex-1 max-w-md">
            <CTextField
              placeholder="Search skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
              startIcon={<SearchIcon className="text-blue-300" />}
            />
          </div>
          <CButton
            onClick={() => navigate("/assign-critical-training")}
            disabled={criticalSkills.length === 0}
            className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700"
            startIcon={<AddIcon />}
          >
            View Training
          </CButton>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          <CDataGrid
            idKey="id"
            rows={filteredCriticalSkills.filter((skill) => skill && skill.id)}
            columns={criticalSkillsColumns}
            loading={loading}
            initialPageSize={10}
            pageSize={10}
            height="100%"
          />
        </div>
      </div>
    </div>
  );
};

export default CriticalSkills;

